import posthog from "posthog-js";

class PostHogService {
  constructor() {
    this.posthog = null;
    this.isInitialized = false;
  }

  init(token, options = {}) {
    if (this.isInitialized) {
      console.warn("PostHog is already initialized");
      return;
    }

    if (!token || token === "YOUR_TOKEN") {
      console.warn(
        "PostHog token is not provided or is placeholder. PostHog will not be initialized."
      );
      return;
    }

    try {
      // Configuration optimized for Capacitor mobile app
      const defaultOptions = {
        api_host: "https://us.i.posthog.com",
        person_profiles: "identified_only", // Only create profiles for identified users
        capture_pageview: false, // Disable automatic pageview - handle manually like Mixpanel
        autocapture: false, // Disable autocapture in mobile WebView - track manually
        persistence: "localStorage", // Use localStorage only (more reliable in Capacitor)
        secure_cookie: false, // Cookies don't work well in mobile WebView
        loaded: (posthog) => {
          console.log("PostHog loaded successfully in Capacitor WebView");
        },
        ...options,
      };

      posthog.init(token, defaultOptions);
      this.posthog = posthog;
      this.isInitialized = true;

      console.log("PostHog initialized successfully for Capacitor app");
    } catch (error) {
      console.error("Failed to initialize PostHog:", error);
    }
  }

  track(eventName, properties = {}) {
    if (!this.isInitialized) {
      console.warn("PostHog is not initialized. Event not tracked:", eventName);
      return;
    }

    try {
      this.posthog.capture(eventName, properties);
      // Simple log for development - just show the event name
      if (process.env.NODE_ENV === "development") {
        console.log(`📊 PostHog: ${eventName}`);
      }
    } catch (error) {
      console.error("Failed to track event:", eventName, error);
    }
  }

  identify(userId, properties = {}) {
    if (!this.isInitialized) {
      console.warn("PostHog is not initialized. User not identified:", userId);
      return;
    }

    try {
      this.posthog.identify(userId, properties);
    } catch (error) {
      console.error("Failed to identify user:", userId, error);
    }
  }

  setUserProperties(properties) {
    if (!this.isInitialized) {
      console.warn("PostHog is not initialized. User properties not set");
      return;
    }

    try {
      this.posthog.people.set(properties);
    } catch (error) {
      console.error("Failed to set user properties:", error);
    }
  }

  alias(newId) {
    if (!this.isInitialized) {
      console.warn("PostHog is not initialized. Alias not created");
      return;
    }

    try {
      this.posthog.alias(newId);
    } catch (error) {
      console.error("Failed to create alias:", error);
    }
  }

  reset() {
    if (!this.isInitialized) {
      console.warn("PostHog is not initialized. Reset not performed");
      return;
    }

    try {
      this.posthog.reset();
    } catch (error) {
      console.error("Failed to reset PostHog:", error);
    }
  }

  // Utility method to track page views manually if needed
  trackPageView(pageName, properties = {}) {
    this.track("$pageview", {
      $current_url: window.location.href,
      page: pageName,
      ...properties,
    });
  }

  // Privacy compliance methods
  optOutTracking() {
    if (!this.isInitialized) {
      console.warn("PostHog is not initialized. Opt-out not performed");
      return;
    }

    try {
      this.posthog.opt_out_capturing();
    } catch (error) {
      console.error("Failed to opt out of tracking:", error);
    }
  }

  optInTracking() {
    if (!this.isInitialized) {
      console.warn("PostHog is not initialized. Opt-in not performed");
      return;
    }

    try {
      this.posthog.opt_in_capturing();
    } catch (error) {
      console.error("Failed to opt in to tracking:", error);
    }
  }

  // Feature flags
  isFeatureEnabled(featureKey) {
    if (!this.isInitialized) {
      console.warn("PostHog is not initialized. Feature flag not checked");
      return false;
    }

    try {
      return this.posthog.isFeatureEnabled(featureKey);
    } catch (error) {
      console.error("Failed to check feature flag:", error);
      return false;
    }
  }

  onFeatureFlags(callback) {
    if (!this.isInitialized) {
      console.warn(
        "PostHog is not initialized. Feature flags callback not set"
      );
      return;
    }

    try {
      this.posthog.onFeatureFlags(callback);
    } catch (error) {
      console.error("Failed to set feature flags callback:", error);
    }
  }
}

// Export a singleton instance
const postHogService = new PostHogService();
export default postHogService;
